/**
 * `/api/notifications` — channels, routing rules, the catalogue, and the queue.
 *
 * One thing to keep in mind everywhere this is used: **a channel's `config`
 * comes back masked.** The webhook token is stars, the SMTP password is stars.
 * Secrets travel inward only, so a channel editor cannot pre-fill them — to
 * change one you set it again rather than reading it back and editing it.
 */
import type {
  AudienceResolverListResponse,
  ChannelListResponse,
  ChannelResponse,
  CreateChannelRequest,
  CreateRuleRequest,
  DeleteChannelResponse,
  DeliveryListResponse,
  DrainResponse,
  ListQueryParams,
  RuleListResponse,
  RuleResponse,
  SendTestRequest,
  SendTestResponse,
  TemplateListResponse,
  TransportListResponse,
  UpdateChannelRequest,
  UpdateRuleRequest,
} from '@forsys/shared';
import { apiFetch, query } from './client';

// --- the catalogue ----------------------------------------------------------

/** What each transport can carry, so a channel editor can show the limits up front. */
export function listTransports(): Promise<TransportListResponse> {
  return apiFetch<TransportListResponse>('/notifications/transports');
}

/**
 * The ways a rule can work out who to tell.
 *
 * Read from the server rather than listed here, for the same reason
 * `listTransports` is: a resolver this build does not have must not appear in
 * the dropdown, and a copy here would drift from the registry.
 */
export function listAudienceResolvers(): Promise<AudienceResolverListResponse> {
  return apiFetch<AudienceResolverListResponse>('/notifications/audiences');
}

/** The message templates, and which event each answers to. */
export function listTemplates(): Promise<TemplateListResponse> {
  return apiFetch<TemplateListResponse>('/notifications/templates');
}

// --- channels ---------------------------------------------------------------

export function listChannels(params: ListQueryParams = {}): Promise<ChannelListResponse> {
  return apiFetch<ChannelListResponse>(`/notifications/channels${query({ ...params })}`);
}

export function getChannel(id: number): Promise<ChannelResponse> {
  return apiFetch<ChannelResponse>(`/notifications/channels/${id}`);
}

export function createChannel(body: CreateChannelRequest): Promise<ChannelResponse> {
  return apiFetch<ChannelResponse>('/notifications/channels', { method: 'POST', json: body });
}

/** Omitted fields are left alone — including `config`, which stays as it was. */
export function updateChannel(id: number, body: UpdateChannelRequest): Promise<ChannelResponse> {
  return apiFetch<ChannelResponse>(`/notifications/channels/${id}`, {
    method: 'PATCH',
    json: body,
  });
}

/** Rules cascade. `rulesRemoved` is how many went with it — say so before asking. */
export function deleteChannel(id: number): Promise<DeleteChannelResponse> {
  return apiFetch<DeleteChannelResponse>(`/notifications/channels/${id}`, { method: 'DELETE' });
}

/** Queues a test message to this channel and delivers it immediately. */
export function sendChannelTest(id: number, body: SendTestRequest = {}): Promise<SendTestResponse> {
  return apiFetch<SendTestResponse>(`/notifications/channels/${id}/test`, {
    method: 'POST',
    json: body,
  });
}

// --- rules ------------------------------------------------------------------

export function listRules(params: ListQueryParams = {}): Promise<RuleListResponse> {
  return apiFetch<RuleListResponse>(`/notifications/rules${query({ ...params })}`);
}

export function createRule(body: CreateRuleRequest): Promise<RuleResponse> {
  return apiFetch<RuleResponse>('/notifications/rules', { method: 'POST', json: body });
}

/**
 * A partial edit: send only what changes.
 *
 * Posting the whole rule back would work, but the audit row records both sides
 * of every field — so a form that always sends all six makes "this now routes
 * somewhere else" and "somebody fixed a typo" look alike in the log.
 */
export function updateRule(id: number, body: UpdateRuleRequest): Promise<RuleResponse> {
  return apiFetch<RuleResponse>(`/notifications/rules/${id}`, { method: 'PATCH', json: body });
}

/** The switch in the table: `updateRule` with the one field it touches. */
export function setRuleEnabled(id: number, isEnabled: boolean): Promise<RuleResponse> {
  return updateRule(id, { isEnabled });
}

export function deleteRule(id: number): Promise<{ removed: boolean }> {
  return apiFetch<{ removed: boolean }>(`/notifications/rules/${id}`, { method: 'DELETE' });
}

// --- the queue --------------------------------------------------------------

/** `status` is `pending` | `sent` | `failed` | `dead`. */
export function listDeliveries(
  options: ListQueryParams & { status?: string } = {},
): Promise<DeliveryListResponse> {
  return apiFetch<DeliveryListResponse>(`/notifications/deliveries${query({ ...options })}`);
}

export function retryDelivery(id: number): Promise<{ requeued: boolean }> {
  return apiFetch<{ requeued: boolean }>(`/notifications/deliveries/${id}/retry`, {
    method: 'POST',
  });
}

/**
 * Delivers whatever is due, once, now.
 *
 * Not a test: this flushes **real** queued messages to real destinations, which
 * is why it needs `notification.manage` rather than `notification.send_test`.
 */
export function drainQueue(): Promise<DrainResponse> {
  return apiFetch<DrainResponse>('/notifications/drain', { method: 'POST' });
}
